import React, { useState } from 'react';
import { Modal, Button, Dropdown } from '@tcc/crate';
import { getMessage } from 'util/locale';
import { useAppSelector, useAppDispatch } from 'app/hooks';
import { hideCreateJDWorkPlanModal } from 'current/uiwidgets/index';
import { sendBulkWorkPlansToJohnDeere, sendWorkPlanToJohnDeere, setSelectedJohnDeereField } from 'affiliates';
import { getActiveField, getActiveFarm, getActiveScript } from 'current/selectors';
import { getJohnDeereFields } from 'affiliates/selectors';
import { getOperationByUuid } from 'operations/selectors';
import { getExportParams, isAnyFieldSelectedForWorkPlan } from 'export-selector/selectors';

import WorkPlansFieldsSelector from 'affiliates/WorkPlansFieldsSelector';
import type { JohnDeereField, Script } from 'store/types/types';
import { exportActionTypes } from 'constants/exportSelector';

const formatClientOptions = (johnDeereFields: JohnDeereField[], defaultOption: JohnDeereField['clientName']) => {
  const clients = new Set(johnDeereFields.map(({ clientName }) => clientName));
  const sortedClients = [...clients].sort((clientA, clientB) => clientA.localeCompare(clientB));

  return [defaultOption, ...sortedClients].map((client) => ({ id: client, name: client }));
};

const getScriptType = (primaryOnly?: boolean, activeScript?: Script) => {
  // It is not present in the single script flow
  if (primaryOnly !== undefined) {
    return primaryOnly ? 'primary' : 'all';
  }

  return activeScript?.primary ? 'primary' : 'not-primary';
};

const CreateJDWorkingPlanModal = ({ isModalOpen }: { isModalOpen: boolean }) => {
  const defaultClient = getMessage('john-deere-all-clients');
  const [selectedClient, setSelectedClient] = useState(defaultClient);
  const dispatch = useAppDispatch();
  const { name: field, area, operationId, farm } = useAppSelector(getActiveField);
  const { growerName, name } = useAppSelector(getActiveFarm) || {};
  const fieldOperation = useAppSelector(state => getOperationByUuid(state, operationId));
  const johnDeereFields = useAppSelector(state => getJohnDeereFields(state));
  const selectedJDField = useAppSelector(state => state.affiliates?.deere?.selectedField)
    || johnDeereFields.find(({ name: jdFieldName }) => jdFieldName === field)?.id;
  const isBulkWorkPlansEnabled = useAppSelector((state) => state.launchDarkly.bulkWorkPlans);
  const canCreateWorkPlan = useAppSelector(isAnyFieldSelectedForWorkPlan);
  const { scripts: scriptCount = 1, scopeType = 'single-script', primaryOnly } = useAppSelector(getExportParams);
  const activeScript = useAppSelector(getActiveScript);
  const scriptType = getScriptType(primaryOnly, activeScript);
  const farmName = name ?? farm.name;
  const clientName = growerName ?? farm.growerName;

  const filteredFields = selectedClient === defaultClient
    ? johnDeereFields
    : johnDeereFields.filter(({ clientName: jdFieldClient }) => jdFieldClient === selectedClient);

  const fieldOptions = filteredFields.sort((fieldA: JohnDeereField, fieldB: JohnDeereField) =>
    fieldA.name.localeCompare(fieldB.name)
  );
  const clientOptions = formatClientOptions(johnDeereFields, defaultClient);
  const preventWorkPlanCreation = isBulkWorkPlansEnabled ? !canCreateWorkPlan : !selectedJDField;

  const onCloseModal = () => {
    setSelectedClient(defaultClient);
    dispatch(hideCreateJDWorkPlanModal());
  };

  const createSingleWorkPlan = () => {
    setSelectedClient(defaultClient);
    dispatch(hideCreateJDWorkPlanModal());
    dispatch(sendWorkPlanToJohnDeere(selectedJDField));
  };

  const createWorkPlansInBulk = () => {
    dispatch(hideCreateJDWorkPlanModal());
    dispatch(sendBulkWorkPlansToJohnDeere());
  };

  const onSelectJohnDeereField = (_: string, id: string) => {
    dispatch(setSelectedJohnDeereField(id));
  };

  const onSelectClient = (jdClient: string) => {
    setSelectedClient(jdClient);
    dispatch(setSelectedJohnDeereField());
  };
  return (
    <Modal show={isModalOpen} size='XL'>
      <div className='create-jd-work-plan'>
        <p>{getMessage('generate-jd-work-plan', { scriptCount })}</p>
        {isBulkWorkPlansEnabled
          ? <WorkPlansFieldsSelector />
          : <section className='create-jd-work-plan-fields'>
            <div className='create-jd-work-plan-block'>
              <p> <strong>{getMessage('fieldview')}</strong> </p>
              <p> <small>{getMessage('generate-jd-work-plan-fv')}</small> </p>
              <p>{field}</p>
              <p> <small>{area.q} {area.u} {clientName} {farmName} {fieldOperation.operationName}</small> </p>
            </div>
            <div className='create-jd-work-plan-block'>
              <p> <strong>{getMessage('john-deere')}</strong> </p>
              <p> <small>{getMessage('john-deere-client-selection')}</small> </p>
              <Dropdown
                disabled={clientOptions.length < 3}
                options={clientOptions}
                setValue={onSelectClient}
                selectedId={selectedClient}
                searchable={clientOptions.length > 7}
                searchPlaceholder={getMessage('placeholder-text__search')}
                testId={'john-deere-client-selector'}
              />
              <p> <small>{getMessage('generate-jd-work-plan-jd', { scriptCount })}</small></p>
              <Dropdown
                options={fieldOptions}
                setValue={onSelectJohnDeereField}
                selectedId={selectedJDField}
                isLoading={!fieldOptions?.length}
                placeholder={getMessage('placeholder-text__select-field')}
                searchable={fieldOptions?.length > 6}
                searchPlaceholder={getMessage('placeholder-text__search')}
                testId={'john-deere-field-selector'}
              />
            </div>
          </section>}
        <section className='create-jd-work-plan-buttons'>
          <Button
            data-test-id={`${exportActionTypes.CREATE_WORK_PLAN}-${scopeType}-${scriptType}`}
            mode='primary'
            onClick={isBulkWorkPlansEnabled ? createWorkPlansInBulk : createSingleWorkPlan}
            disabled={preventWorkPlanCreation}>
            {getMessage('create-work-plan', { scriptCount })}
          </Button>
          <Button
            mode='secondary'
            onClick={onCloseModal}>
            {getMessage('button-text__cancel')}
          </Button>
        </section>
      </div>
    </Modal>
  );
};

export default CreateJDWorkingPlanModal;
